/* eslint eqeqeq: [2, "smart"] */

import { produce } from 'immer'
import { forEach, reduce } from 'lodash'
import applyJsonMergePatch from 'tiny-merge-patch'

import { actionTypesFor } from './redux-crud-actions'

/*
 * Generalized handling of basic crud operations on a REST resource for the redux framework.
 * See the documentation of redux under http://redux.js.org/ for a detailed explanation of the appropriate concepts.
 */

/**
 * @param {function|string} key
 * @return {function(array|object): *}
 */
function createKeyValueGetter(key) {
    return function (data) {
        if (!key) {
            // Is/Should actually be checked before.
            throw new Error('No key specified for reducer!')
        }

        if (typeof key === 'function') {
            const value = key(data)
            if (!value) {
                throw new Error(`Invalid key function! Resource: ${JSON.stringify(data)}`)
            }
            return value
        } else if (typeof key === 'string') {
            const value = data[key]
            if (!value) {
                throw new Error(`Missing key ${key}! Resource: ${JSON.stringify(data)}`)
            }
            return value
        } else {
            throw new TypeError('Invalid key type! Key must be a string or function!')
        }
    }
}

/**
 *
 * @param {Object} draft
 * @param {Array} data
 * @param {function} getKeyValue
 * @param {boolean} [patch]
 */
function mergeState(draft, data, getKeyValue, patch = false) {
    const apply = patch
        ? (draft, entry) => {
              const _key = getKeyValue(entry)
              if (!draft[_key]) {
                  draft[_key] = entry
              } else {
                  Object.assign(draft[_key], entry)
              }
          }
        : (draft, entry) => {
              draft[getKeyValue(entry)] = entry
          }

    if (Array.isArray(data)) {
        data.forEach(entry => apply(draft, entry))
    } else {
        apply(draft, data)
    }
}

/**
 * @param {*} parentKeyValue
 * @return {*}
 */
const createSubDraftGetter = parentKeyValue => rawDraft => {
    if (!rawDraft[parentKeyValue]) {
        rawDraft[parentKeyValue] = {}
    }

    return rawDraft[parentKeyValue]
}

/**
 * @param {string|Function} key
 * @param {Object} initialState
 * @param {Object} actionTypes
 * @param {Boolean} [preventAdd]
 * @return {ReducerBaseHandler}
 */
function getReducerBaseHandler(key, initialState, actionTypes, preventAdd = false) {
    const getKeyValue = createKeyValueGetter(key)

    /**
     * @typedef ReducerBaseHandler
     *
     * @param {object} draft
     * @param {*} payload
     * @param {string} type
     * @param {boolean} [resetState]
     * @param {Function} [getDraft]
     *
     * @return *
     */
    return (rawDraft, payload, type, resetState = false, parentKeyValue) => {
        const getDraft = parentKeyValue ? createSubDraftGetter(parentKeyValue) : rawDraft => rawDraft
        const draft = getDraft(rawDraft)

        switch (type) {
            case actionTypes.fetchSuccess:
            case actionTypes.updateSuccess:
            case actionTypes.createSuccess: {
                if (!key) {
                    if (parentKeyValue) {
                        rawDraft[parentKeyValue] = payload
                        return
                    } else {
                        return payload
                    }
                }

                if (resetState) {
                    const result = produce(initialState, draft => {
                        mergeState(getDraft(draft), payload, getKeyValue)
                    })
                    if (parentKeyValue) {
                        rawDraft[parentKeyValue] = result[parentKeyValue]
                    } else {
                        return result
                    }
                    return
                }

                mergeState(draft, payload, getKeyValue)
                break
            }
            case actionTypes.patchSuccess: {
                // Similar to fetchSuccess, but overwrites only fields existent in action.data
                if (!key && Array.isArray(payload)) {
                    throw new Error('Patching of arrays w/o a key is not supported!')
                }

                if (!key) {
                    Object.assign(draft, payload)
                    return
                }

                mergeState(draft, payload, getKeyValue, true)
                break
            }
            case actionTypes.patchMergePatchJsonSuccess: {
                // Similar to fetchSuccess, but overwrites only fields existent in action.data
                // Fields defined as "null" in patch object will be removed from state
                // according to the JSON Merge Patch definition.
                Object.keys(payload).forEach(_key => {
                    if (payload[_key]) {
                        if (!preventAdd || draft[_key]) {
                            draft[_key] = applyJsonMergePatch(draft[_key], payload[_key])
                        }
                    } else {
                        delete draft[_key]
                    }
                })
                break
            }
            case actionTypes.deleteSuccess: {
                if (!key) {
                    throw new Error('Cannot remove elements w/o a key!')
                }
                if (Array.isArray(payload)) {
                    payload.forEach(entry => {
                        const _key = getKeyValue(entry)
                        delete draft[_key]
                    })
                } else {
                    const _key = getKeyValue(payload)
                    delete draft[_key]
                }
                break
            }
        }
    }
}

/**
 * Creates a general reducer function for the basic crud operations on a REST resource.
 * The entities of a REST resource collection are stored in an object with a unique id as key and the entity
 * itself as value. The name of the unique id attribute can be specified via the key parameter. The key
 * parameter can also be a function that is passed the entity and returns a unique id as key which can be
 * assembled from multiple entity attributes.
 * The objects returned by the redux-reducer function are immutable.
 *
 * @example
 * // key is a single entity attribute providing a unique id
 * const reducer = reducerFor('myResource', 'myResourceId')
 *
 * // key is a function which assembles a unique id from multiple entity attributes and/or other data
 * const reducer = reducerFor('myResource', (entity) => {
 *     return entity.name + '-' + entity.creationTime
 * })
 *
 * // this fetch result
 * [
 *  { entityId: "123", name="foo" },
 *  { entityId: "321", name="bar" }
 * ]
 * // will be transformed to this collection structure:
 * {
 *  "123": { entityId: "123", name="foo" },
 *  "321": { entityId: "321", name="bar" }
 * }
 *
 * @param {string} resourceName             - An unique name to be used for the appropriate REST resource.
 * @param {string|function} key           - Either the attribute name under which the unique entity id can be found
 *                                            or a function which assembles and returns a unique id for a given entity.
 * @param {Object} [initialState={}]        - An optional initial state to be provided to the reducer (only useful for
 *                                            reducers without keys).
 * @param {boolean} [preventAdd]            - Prevent adding of entities to redux store on incremental patches (push)
 * @param {boolean} [isExternalApiResource] - Is the payload contained in the `items` property of the response data?
 * @returns {function} A function, that can act as a redux reducer for 'standard' crud actions.
 */
export function reducerFor(resourceName, key, initialState = {}, preventAdd = false, isExternalApiResource = false) {
    const actionTypes = actionTypesFor(resourceName)
    const handle = getReducerBaseHandler(key, initialState, actionTypes, preventAdd)

    /**
     * The actual reducer function for the REST resource. This is called by the redux dispatch function to actually
     * update the appropriate state in the store.
     *
     * @param {Object} draft
     * @param {Object} action
     * @property {Object} action.data
     * @property {Boolean} action.resetState
     * @property {String} action.type
     *
     * @return {Object|undefined}
     */
    const recipe = (draft, action) => {
        const { data, resetState, type } = action

        if (!data) {
            return
        }

        // Our external APIs respond with { items: [...], ... }, where items contain the actual payload,
        // whereas our internal APIs just respond with an array containing the payload
        let payload
        if (
            isExternalApiResource &&
            type === actionTypes.fetchSuccess &&
            !Array.isArray(data) &&
            data.items &&
            Array.isArray(data.items)
        ) {
            payload = data.items
        } else {
            payload = data
        }

        return handle(draft, payload, type, resetState)
    }
    return produce(recipe, initialState)
}

/**
 * Creates a reducer function for the basic crud operations on a REST subresource.
 * The entities of a REST resource collection are stored in an object with a unique id of the parent resource as key.
 * The value will be the list of entities of the subresource with the unique id of the entities as key.
 * The name of the unique id attribute of the subresource and the parent resource can be specified via the key parameters.
 * The key parameter can also be a function that is passed the entity and returns a unique id as key which can be
 * assembled from multiple entity attributes.
 * The objects returned by the redux-reducer function are immutable.
 *
 * @example
 * // with parentKey and key as a single entity attribute providing a unique id
 * const reducer = reducerForSubresource('myResource', 'parentId', 'entityId')
 *
 * // this fetch result
 * [
 *  { entityId: "123", parentId: "AAA" name="foo" },
 *  { entityId: "456", parentId: "AAA" name="fooFoo" },
 *  { entityId: "321", parentId: "BBB" name="bar" }
 * ]
 * // will be transformed to this collection structure:
 * {
 *  "AAA": {
 *      "123": { entityId: "123", parentId: "AAA", name="foo" },
 *      "456": { entityId: "456", parentId: "AAA", name="fooFoo" }
 *  },
 *  "BBB": {
 *      "321": { entityId: "321", parentId: "BBB" name="bar" }
 *  }
 * }
 *
 * @param {string} resourceName             - An unique name to be used for the appropriate REST resource.
 * @param {string|function} [parentKey]       - Either the attribute name under which the unique parent entity id can be
 *                                            found or a function which assembles and returns a unique id for a given
 *                                            parent entity.
 * @param {string|function} [key]             - Either the attribute name under which the unique entity id can be found
 *                                            or a function which assembles and returns a unique id for a given entity.
 * @param {Object} [initialState={}]        - An optional initial state to be provided to the reducer (only useful for
 *                                            reducers without keys).
 * @param {boolean} [preventAdd]            - Prevent adding of entities to redux store on incremental patches (push)
 * @returns {function} A function, that can act as a redux reducer for 'standard' crud actions.
 */
export function reducerForSubresource(resourceName, parentKey, key = undefined, initialState = {}, preventAdd = false) {
    if (!resourceName) {
        throw new Error('No resource name specified for subresource reducer!')
    }

    if (!parentKey) {
        throw new Error('No parent key specified for subresource reducer!')
    }

    const actionTypes = actionTypesFor(resourceName)
    const getParentKeyValue = createKeyValueGetter(parentKey)
    const handle = getReducerBaseHandler(key, initialState, actionTypes, preventAdd)

    /**
     * The actual reducer function for the REST resource. This is called by the redux dispatch function to actually
     * update the appropriate state in the store.
     *
     * @param {Object} stateDraft
     * @param {Object} action
     * @property {Object} action.data
     * @property {String} action.parentKey
     * @property {Boolean} action.resetState
     * @property {String} action.type
     *
     * @return {Object|undefined}
     */
    const recipe = (stateDraft, action) => {
        const { data, parentKey: parentKeyValue, resetState, type } = action

        if (!data) {
            return
        }

        const isSubresourceAction = !!parentKeyValue

        if (isSubresourceAction) {
            if (stateDraft[parentKeyValue] === undefined) {
                stateDraft[parentKeyValue] = {}
            }

            // just apply handling on subresources of the according parent entity
            return handle(stateDraft, data, type, resetState, parentKeyValue)
        } else {
            // flatten the draft for actions without parentKeyValue (f.e. bulk actions)
            let flatDraft = reduce(
                stateDraft,
                (accumulator, value) => {
                    return Object.assign(accumulator, value)
                },
                {}
            )
            const originalFlatDraft = { ...flatDraft }
            // handle action for all subresources in flat structure
            const handleResult = handle(flatDraft, data, type, resetState)
            if (handleResult !== undefined) {
                flatDraft = handleResult
            }
            // apply changes from the handling to the actual draft structure
            // add and update items
            forEach(flatDraft, (value, key) => {
                const parentKeyValue = getParentKeyValue(value)

                if (!stateDraft[parentKeyValue]) {
                    stateDraft[parentKeyValue] = {}
                }

                stateDraft[parentKeyValue][key] = value
            })
            // delete items
            forEach(originalFlatDraft, (value, key) => {
                if (flatDraft[key] === undefined) {
                    const parentKeyValue = getParentKeyValue(value)
                    delete stateDraft[parentKeyValue][key]
                }
            })
        }
    }
    return produce(recipe, initialState)
}

/**
 * Creates a general reducer function for the READ, UPDATE, PARTIAL_UPDATE and DELETE operations on a Singleton REST resource.
 * The entities of a REST resource collection are stored in an object with the unique id of the parent entity
 * as key and the entity itself as value. The unique id of the parent entity is provided via the action by the
 * generic methods createSubResourceActions and createActionVerbAction of a bound api action object.
 *
 * @example
 * const actions = createBoundActions('myParentResource', 'api', '/somePath', 'uniqueId')
 * const createSingleton = (parentEntity) => actions.createSubResourceActions(parentEntity, 'singletonResource', '/singletonPath')
 * const fetchSingleton = (parentEntity) => createSingleton(parentEntity).fetch()
 * const updateSingleton = (parentEntity, data) => createSingleton(parentEntity).update(data)
 * const deleteSingleton = (parentEntity) => createSingleton(parentEntity).delete()
 * const partialUpdateSingleton = (parentEntity, data) => createSingleton(parentEntity).partialUpdate(data)
 *
 * export reducers = {
 *    parent: reducerFor('myParentResource', 'uniqueId'),
 *    singleton: reducerForSingletonSubresource('singletonResource') // {[parentId]: { ...singletonEntity }}
 * }
 *
 * @param {string} resourceName         - An unique name to be used for the appropriate REST resource.
 * @param {Object} [initialState={}]    - An optional initial state to be provided to the reducer (only useful for
 *                                        reducers without keys).
 * @returns {function} A function, that can act as a redux reducer for 'standard' singleton rud actions.
 */
export function reducerForSingletonSubresource(resourceName, initialState = {}) {
    const actionTypes = actionTypesFor(resourceName)

    /**
     * The actual reducer function for the REST Singleton resource. This is called by the redux dispatch function to actually
     * update the appropriate state in the store.
     */
    return produce((draft, action) => {
        const { data, parentKey } = action

        switch (action.type) {
            case actionTypes.fetchSuccess:
            case actionTypes.updateSuccess: {
                if (!parentKey) {
                    return
                }
                draft[parentKey] = data
                break
            }
            case actionTypes.deleteSuccess: {
                if (!parentKey) {
                    return
                }
                delete draft[parentKey]
                break
            }
        }
    }, initialState)
}

/**
 * Convenience function for setting up an reducer object that can be merged with other reducer objects
 * and then passed to function combineReducers of Redux.
 *
 * @param {string} resourceName             - A unique name to be used for the appropriate REST resource.
 * @param {string|function} [key]           - Either the attribute name under which the unique entity id can be found
 *                                            or a function which assembles and returns a unique id for a given entity.
 * @param {Object} [initialState={}]        - An optional initial state to be provided to the reducer (only useful for
 *                                            reducers without keys).
 * @param {boolean} [preventAdd]            - Prevent adding of entities to redux store on incremental patches (push)
 * @param {boolean} [isExternalApiResource] - Is the payload contained in the `items` property of the response data?
 * @returns {Object} An object that maps the resource name to the a created reducer function.
 */
export function reducerObjectFor(
    resourceName,
    key,
    initialState = {},
    preventAdd = false,
    isExternalApiResource = false
) {
    return { [resourceName]: reducerFor(resourceName, key, initialState, preventAdd, isExternalApiResource) }
}

/**
 * Returns a object which contains a reducer with a virtual key and a function to update the this key.
 *
 * @example
 * // Create the reducer helper object.
 * const myReducerHandler = reducerForDynamicKey('SomeResourceName')
 *
 * // Create the actions which also updating the key.
 * function createSomeActions (someId) {
 *      // Update the key.
 *      myReducerHandler.setKey(someId)
 *      // return the created actions
 *      return createBoundActions('SomeResourceName', '/api', '/foo', someId)
 * }
 *
 * // Create the store - note the '.reduce'
 * const store = createStore(combineReducers({ SomeResourceName: myReducerHandler.reduce })
 *
 * // Fetch new data.
 * dispatch(createSomeActions.fetch('1234')) // The state will now look like: { '1234': *resultData* }
 *
 * // You can have a look at a implementation at atlantis
 * // https://bitbucket.ttt-sp.com/projects/WEBFLEET/repos/atlantis/commits/a84f4d0d9c9a13ece4a3d19f6abf3d41c6c3f08a
 *
 * @param {string} resourceName             - A unique name to be used for the appropriate REST resource.
 * @param {string|function} [key]           - The virtual name under which resources can be found.
 * @param {Object} [initialState={}]        - An optional initial state to be provided to the reducer.
 * @param {boolean} [preventAdd]            - Prevent adding of entities to redux store on incremental patches (push)
 * @param {boolean} [isExternalApiResource] - Is the payload contained in the `items` property of the response data?
 * @returns {{reduce: function(Object, string), setKey: function(string)}}
 */
export function reducerForDynamicKey(
    resourceName,
    key,
    initialState = {},
    preventAdd = false,
    isExternalApiResource = false
) {
    let keyValue
    const reducer = reducerFor(resourceName, key, initialState, preventAdd, isExternalApiResource)

    return {
        reduce: (state = initialState, action) => {
            if (state && keyValue) {
                return {
                    [keyValue]: reducer(state[keyValue], action)
                }
            }
            return reducer(state, action)
        },
        setKey: newKeyValue => {
            keyValue = newKeyValue
        }
    }
}
